import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  AlertTriangle, Shield, Scale, TrendingUp, User, Building2, Search,
  FileText, Gavel, ChevronRight, ChevronDown, CheckCircle, XCircle,
  Clock, DollarSign, AlertOctagon, Info, Zap, Target, BarChart3,
  PieChart, Activity, Eye, Loader2, Copy, RefreshCw, Users, MessageSquare,
  ThumbsUp, ThumbsDown, ArrowRight, Calendar, MapPin, CreditCard, Download,
  ShieldCheck, ArrowLeftRight, ArrowLeft, Home
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import api from '@/services/api';
import { toast } from 'sonner';
import { generateLitigationPDF, generateIdentityPDF, downloadPDF, requestVerificationCode } from '@/utils/pdfGenerator';
import { Link } from 'react-router-dom';
import ClauseNegotiator from '@/components/ClauseNegotiator';

// ==================== LITIGATION PREDICTOR COMPONENT ====================

const LitigationPredictor = ({ contractId, content }) => {
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [expanded, setExpanded] = useState({});

  const analyzeContract = async () => {
    setLoading(true);
    try {
      const { data } = await api.post('/litigation/analyze', {
        contract_id: contractId,
        content: content,
        contract_type: 'general'
      });
      setAnalysis(data);
      toast.success('Análisis de litigios completado');
    } catch (error) {
      toast.error('Error al analizar');
    }
    setLoading(false);
  };

  const exportPDF = async () => {
    if (!analysis) return;
    setExporting(true);
    try {
      // Request verification code from backend
      const verificationData = await requestVerificationCode('litigation', analysis, {
        analysis_date: analysis.analysis_date,
        risk_level: analysis.risk_level
      });
      
      // Generate PDF with verification code
      const doc = await generateLitigationPDF(analysis, verificationData.verification_code);
      downloadPDF(doc, `analisis-litigios-${verificationData.verification_code}.pdf`);
      toast.success(`PDF verificado generado. Código: ${verificationData.verification_code}`);
    } catch (error) {
      console.error('Export error:', error);
      // Fallback without verification
      const doc = await generateLitigationPDF(analysis, null);
      downloadPDF(doc, `analisis-litigios-${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success('PDF descargado');
    }
    setExporting(false);
  };

  const getRiskColor = (level) => {
    const colors = {
      'CRÍTICO': 'from-red-600 to-red-800',
      'ALTO': 'from-orange-500 to-orange-700',
      'MODERADO': 'from-yellow-500 to-yellow-600',
      'BAJO': 'from-green-500 to-green-600'
    };
    return colors[level] || 'from-gray-500 to-gray-600';
  };

  return (
    <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-red-500/20 rounded-lg">
            <AlertTriangle className="w-6 h-6 text-red-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">Predictor de Litigios</h3>
            <p className="text-sm text-slate-400">Análisis predictivo de riesgo de demandas</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          {analysis && (
            <button
              onClick={exportPDF}
              disabled={exporting}
              className="px-3 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg flex items-center space-x-2 disabled:opacity-50"
              title="Exportar PDF con código de verificación legal"
            >
              {exporting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <ShieldCheck className="w-4 h-4" />
              )}
              <span>PDF Verificado</span>
            </button>
          )}
          <button
            onClick={analyzeContract}
            disabled={loading}
            className="px-4 py-2 bg-red-600 hover:bg-red-500 text-white rounded-lg flex items-center space-x-2 disabled:opacity-50"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
            <span>Analizar</span>
          </button>
        </div>
      </div>

      {analysis && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-6"
        >
          {/* Main Score */}
          <div className={`bg-gradient-to-r ${getRiskColor(analysis.risk_level)} rounded-xl p-6`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/80 text-sm">Probabilidad de Litigio</p>
                <p className="text-5xl font-bold text-white">{analysis.litigation_probability}%</p>
                <p className="text-white/90 font-medium mt-1">Riesgo {analysis.risk_level}</p>
              </div>
              <div className="text-right">
                <p className="text-white/80 text-sm">Daños Potenciales</p>
                <p className="text-2xl font-bold text-white">
                  ${analysis.potential_damages?.average?.toLocaleString() || 0}
                </p>
                <p className="text-white/70 text-xs">
                  Rango: ${analysis.potential_damages?.min_estimate?.toLocaleString()} - ${analysis.potential_damages?.max_estimate?.toLocaleString()}
                </p>
              </div>
            </div>
          </div>

          {/* Risks Found */}
          {analysis.risks_found?.length > 0 && (
            <div>
              <h4 className="text-white font-medium mb-3 flex items-center space-x-2">
                <AlertOctagon className="w-4 h-4 text-red-400" />
                <span>Factores de Riesgo Detectados ({analysis.risks_found.length})</span>
              </h4>
              <div className="space-y-2">
                {analysis.risks_found.map((risk, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: idx * 0.1 }}
                    className={`p-3 rounded-lg border ${
                      risk.severity === 'alto' 
                        ? 'bg-red-500/10 border-red-500/30' 
                        : 'bg-yellow-500/10 border-yellow-500/30'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            risk.severity === 'alto' ? 'bg-red-500/20 text-red-400' : 'bg-yellow-500/20 text-yellow-400'
                          }`}>
                            {risk.severity.toUpperCase()}
                          </span>
                          <span className="text-sm text-slate-300 font-medium">{risk.factor}</span>
                        </div>
                        <p className="text-xs text-slate-400 mt-1">{risk.suggestion}</p>
                      </div>
                      <span className="text-xs text-slate-500 bg-slate-700/50 px-2 py-1 rounded">
                        +{risk.weight}% riesgo
                      </span>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          )}

          {/* Protections */}
          {analysis.protections_found?.length > 0 && (
            <div>
              <h4 className="text-white font-medium mb-3 flex items-center space-x-2">
                <Shield className="w-4 h-4 text-green-400" />
                <span>Protecciones Encontradas</span>
              </h4>
              <div className="flex flex-wrap gap-2">
                {analysis.protections_found.map((prot, idx) => (
                  <span key={idx} className="px-3 py-1 bg-green-500/20 text-green-400 rounded-full text-sm">
                    {prot.factor}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Similar Cases */}
          {analysis.similar_cases?.length > 0 && (
            <div>
              <h4 className="text-white font-medium mb-3 flex items-center space-x-2">
                <Gavel className="w-4 h-4 text-blue-400" />
                <span>Jurisprudencia Similar</span>
              </h4>
              <div className="space-y-2">
                {analysis.similar_cases.slice(0, 3).map((caso, idx) => (
                  <div key={idx} className="p-3 bg-slate-700/50 rounded-lg">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-white">{caso.id}</span>
                      <span className="text-xs text-slate-400">{caso.tribunal}</span>
                    </div>
                    <p className="text-xs text-slate-300">{caso.resumen}</p>
                    <div className="flex items-center justify-between mt-2">
                      <span className={`text-xs px-2 py-0.5 rounded ${
                        caso.resultado.includes('Favorable') ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                      }`}>
                        {caso.resultado}
                      </span>
                      {caso.indemnizacion > 0 && (
                        <span className="text-xs text-slate-400">
                          Indemnización: ${caso.indemnizacion.toLocaleString()}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recommendations */}
          {analysis.recommendations?.length > 0 && (
            <div>
              <h4 className="text-white font-medium mb-3 flex items-center space-x-2">
                <Target className="w-4 h-4 text-purple-400" />
                <span>Recomendaciones</span>
              </h4>
              <div className="space-y-2">
                {analysis.recommendations.map((rec, idx) => (
                  <div key={idx} className="flex items-start space-x-3 p-3 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                    <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                      rec.priority === 'URGENTE' ? 'bg-red-500 text-white' :
                      rec.priority === 'IMPORTANTE' ? 'bg-orange-500 text-white' :
                      'bg-blue-500 text-white'
                    }`}>
                      {rec.priority}
                    </span>
                    <div className="flex-1">
                      <p className="text-sm text-white">{rec.action}</p>
                      <p className="text-xs text-slate-400 mt-0.5">{rec.impact}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </motion.div>
      )}
    </div>
  );
};

// ==================== POWER MAP COMPONENT ====================

const PowerMapAnalyzer = ({ contractId, content }) => {
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [partyAName, setPartyAName] = useState('Parte A');
  const [partyBName, setPartyBName] = useState('Parte B');

  const analyzeBalance = async () => {
    setLoading(true);
    try {
      const { data } = await api.post('/power-map/analyze', {
        contract_id: contractId,
        content: content,
        party_a_name: partyAName,
        party_b_name: partyBName
      });
      setAnalysis(data);
      toast.success('Análisis de poder completado');
    } catch (error) {
      toast.error('Error al analizar');
    }
    setLoading(false);
  };

  const getBalanceColor = (score) => {
    if (score >= 40) return 'text-green-400';
    if (score >= 25) return 'text-yellow-400';
    return 'text-red-400';
  };

  return (
    <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-purple-500/20 rounded-lg">
            <Scale className="w-6 h-6 text-purple-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">Mapa de Poder Contractual</h3>
            <p className="text-sm text-slate-400">Análisis de equilibrio entre las partes</p>
          </div>
        </div>
      </div>

      {/* Party Names Input */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <label className="text-xs text-slate-400">Nombre Parte A</label>
          <input
            type="text"
            value={partyAName}
            onChange={(e) => setPartyAName(e.target.value)}
            className="w-full mt-1 px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm"
          />
        </div>
        <div>
          <label className="text-xs text-slate-400">Nombre Parte B</label>
          <input
            type="text"
            value={partyBName}
            onChange={(e) => setPartyBName(e.target.value)}
            className="w-full mt-1 px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm"
          />
        </div>
      </div>

      <button
        onClick={analyzeBalance}
        disabled={loading}
        className="w-full py-2 bg-purple-600 hover:bg-purple-500 text-white rounded-lg flex items-center justify-center space-x-2 disabled:opacity-50 mb-6"
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <BarChart3 className="w-4 h-4" />}
        <span>Analizar Balance de Poder</span>
      </button>

      {analysis && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-6"
        >
          {/* Equity Score Gauge */}
          <div className="bg-slate-700/50 rounded-xl p-6 text-center">
            <p className="text-slate-400 text-sm mb-2">Score de Equidad</p>
            <div className="relative w-32 h-32 mx-auto mb-4">
              <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
                <circle
                  cx="50" cy="50" r="45"
                  fill="none" stroke="#334155" strokeWidth="10"
                />
                <circle
                  cx="50" cy="50" r="45"
                  fill="none"
                  stroke={analysis.equity_score >= 40 ? '#22c55e' : analysis.equity_score >= 25 ? '#eab308' : '#ef4444'}
                  strokeWidth="10"
                  strokeDasharray={`${analysis.equity_score * 2.83} 283`}
                  strokeLinecap="round"
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className={`text-3xl font-bold ${getBalanceColor(analysis.equity_score)}`}>
                  {analysis.equity_score}
                </span>
              </div>
            </div>
            <p className={`font-medium ${
              analysis.balance_status === 'equilibrado' ? 'text-green-400' :
              analysis.balance_status === 'inclinado' ? 'text-yellow-400' : 'text-red-400'
            }`}>
              {analysis.balance_status.toUpperCase()}
            </p>
            {analysis.advantage.party !== 'Ninguna (equilibrado)' && (
              <p className="text-sm text-slate-400 mt-1">
                Ventaja {analysis.advantage.level} para: <span className="text-white">{analysis.advantage.party}</span>
              </p>
            )}
          </div>

          {/* Power Comparison */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 text-center">
              <p className="text-blue-400 font-medium">{partyAName}</p>
              <p className="text-3xl font-bold text-white mt-2">{Math.round(analysis.party_a_score)}</p>
              <p className="text-xs text-slate-400">Puntos de poder</p>
            </div>
            <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 text-center">
              <p className="text-green-400 font-medium">{partyBName}</p>
              <p className="text-3xl font-bold text-white mt-2">{Math.round(analysis.party_b_score)}</p>
              <p className="text-xs text-slate-400">Puntos de poder</p>
            </div>
          </div>

          {/* Abusive Clauses Alert */}
          {analysis.abusive_clauses?.length > 0 && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
              <h4 className="text-red-400 font-medium mb-3 flex items-center space-x-2">
                <AlertOctagon className="w-5 h-5" />
                <span>¡Cláusulas Abusivas Detectadas!</span>
              </h4>
              <div className="space-y-2">
                {analysis.abusive_clauses.map((clause, idx) => (
                  <div key={idx} className="bg-slate-800/50 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-white">{clause.description}</span>
                      <span className={`text-xs px-2 py-0.5 rounded ${
                        clause.severity === 'nula' ? 'bg-red-500 text-white' : 'bg-yellow-500 text-black'
                      }`}>
                        {clause.severity.toUpperCase()}
                      </span>
                    </div>
                    <p className="text-xs text-slate-400">{clause.article}</p>
                    <p className="text-xs text-red-300 mt-1">{clause.legal_consequence}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Clause Analysis */}
          {analysis.clause_analysis?.length > 0 && (
            <div>
              <h4 className="text-white font-medium mb-3">Análisis por Cláusula</h4>
              <div className="space-y-2">
                {analysis.clause_analysis.map((clause, idx) => (
                  <div key={idx} className="bg-slate-700/50 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-white capitalize">{clause.type}</span>
                      <span className={`text-xs px-2 py-0.5 rounded ${
                        clause.balance === 'equilibrado' ? 'bg-green-500/20 text-green-400' :
                        clause.balance === 'favorable_a' ? 'bg-blue-500/20 text-blue-400' :
                        'bg-purple-500/20 text-purple-400'
                      }`}>
                        {clause.balance === 'equilibrado' ? 'Equilibrado' :
                         clause.balance === 'favorable_a' ? `Favorece ${partyAName}` : `Favorece ${partyBName}`}
                      </span>
                    </div>
                    <div className="h-2 bg-slate-600 rounded-full overflow-hidden">
                      <div 
                        className={`h-full rounded-full ${
                          clause.score > 0 ? 'bg-blue-500' : clause.score < 0 ? 'bg-purple-500' : 'bg-green-500'
                        }`}
                        style={{ width: `${50 + clause.score}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recommendations */}
          {analysis.recommendations?.length > 0 && (
            <div>
              <h4 className="text-white font-medium mb-3">Recomendaciones de Equidad</h4>
              <div className="space-y-2">
                {analysis.recommendations.slice(0, 5).map((rec, idx) => (
                  <div key={idx} className={`p-3 rounded-lg border ${
                    rec.priority === 'CRÍTICA' ? 'bg-red-500/10 border-red-500/30' :
                    rec.priority === 'ALTA' ? 'bg-orange-500/10 border-orange-500/30' :
                    'bg-blue-500/10 border-blue-500/30'
                  }`}>
                    <div className="flex items-start space-x-2">
                      <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                        rec.priority === 'CRÍTICA' ? 'bg-red-500 text-white' :
                        rec.priority === 'ALTA' ? 'bg-orange-500 text-white' :
                        'bg-blue-500 text-white'
                      }`}>
                        {rec.priority}
                      </span>
                      <div>
                        <p className="text-sm text-white">{rec.action}</p>
                        {rec.reason && <p className="text-xs text-slate-400 mt-0.5">{rec.reason}</p>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </motion.div>
      )}
    </div>
  );
};

// ==================== SCENARIO SIMULATOR COMPONENT ====================

const ScenarioSimulator = ({ contractId, content }) => {
  const [scenarios, setScenarios] = useState([]);
  const [selectedScenario, setSelectedScenario] = useState('');
  const [contractValue, setContractValue] = useState('');
  const [simulation, setSimulation] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadScenarios();
  }, []);

  const loadScenarios = async () => {
    try {
      const { data } = await api.get('/simulator/scenarios');
      setScenarios(data.scenarios);
      if (data.scenarios.length > 0) {
        setSelectedScenario(data.scenarios[0].id);
      }
    } catch (error) {
      console.error('Error loading scenarios');
    }
  };

  const runSimulation = async () => {
    if (!contractValue) {
      toast.error('Ingresa el valor del contrato');
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post('/simulator/simulate', {
        contract_id: contractId,
        content: content,
        scenario_type: selectedScenario,
        contract_value: parseFloat(contractValue.replace(/\D/g, ''))
      });
      setSimulation(data);
      toast.success('Simulación completada');
    } catch (error) {
      toast.error('Error en la simulación');
    }
    setLoading(false);
  };

  return (
    <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
      <div className="flex items-center space-x-3 mb-6">
        <div className="p-2 bg-amber-500/20 rounded-lg">
          <Activity className="w-6 h-6 text-amber-400" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-white">Simulador de Escenarios</h3>
          <p className="text-sm text-slate-400">¿Qué pasa si...? Simula incumplimientos</p>
        </div>
      </div>

      {/* Configuration */}
      <div className="space-y-4 mb-6">
        <div>
          <label className="text-sm text-slate-400 block mb-1">Escenario a simular</label>
          <select
            value={selectedScenario}
            onChange={(e) => setSelectedScenario(e.target.value)}
            className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
          >
            {scenarios.map(s => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
          {selectedScenario && scenarios.find(s => s.id === selectedScenario) && (
            <p className="text-xs text-slate-500 mt-1">
              {scenarios.find(s => s.id === selectedScenario)?.description}
            </p>
          )}
        </div>

        <div>
          <label className="text-sm text-slate-400 block mb-1">Valor del contrato (CLP)</label>
          <input
            type="text"
            value={contractValue}
            onChange={(e) => setContractValue(e.target.value.replace(/\D/g, '').replace(/\B(?=(\d{3})+(?!\d))/g, '.'))}
            placeholder="10.000.000"
            className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
          />
        </div>

        <button
          onClick={runSimulation}
          disabled={loading || !selectedScenario || !contractValue}
          className="w-full py-2 bg-amber-600 hover:bg-amber-500 text-white rounded-lg flex items-center justify-center space-x-2 disabled:opacity-50"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <TrendingUp className="w-4 h-4" />}
          <span>Simular Escenario</span>
        </button>
      </div>

      {simulation && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-6"
        >
          {/* Risk Assessment */}
          <div className={`rounded-xl p-4 ${
            simulation.risk_assessment.recommendation === 'FAVORABLE' ? 'bg-green-500/10 border border-green-500/30' :
            simulation.risk_assessment.recommendation === 'MODERADO' ? 'bg-yellow-500/10 border border-yellow-500/30' :
            'bg-red-500/10 border border-red-500/30'
          }`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400">Recomendación</p>
                <p className={`text-2xl font-bold ${
                  simulation.risk_assessment.recommendation === 'FAVORABLE' ? 'text-green-400' :
                  simulation.risk_assessment.recommendation === 'MODERADO' ? 'text-yellow-400' :
                  'text-red-400'
                }`}>
                  {simulation.risk_assessment.recommendation}
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm text-slate-400">Probabilidad de éxito</p>
                <p className="text-2xl font-bold text-white">{simulation.risk_assessment.success_probability}%</p>
              </div>
            </div>
          </div>

          {/* Financial Summary */}
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-slate-700/50 rounded-lg p-3 text-center">
              <DollarSign className="w-5 h-5 text-green-400 mx-auto mb-1" />
              <p className="text-xs text-slate-400">Daños Totales</p>
              <p className="text-lg font-bold text-white">
                ${simulation.damages.total_damages?.toLocaleString()}
              </p>
            </div>
            <div className="bg-slate-700/50 rounded-lg p-3 text-center">
              <Scale className="w-5 h-5 text-red-400 mx-auto mb-1" />
              <p className="text-xs text-slate-400">Costos Legales</p>
              <p className="text-lg font-bold text-white">
                ${simulation.costs.total_estimated?.toLocaleString()}
              </p>
            </div>
            <div className="bg-slate-700/50 rounded-lg p-3 text-center">
              <TrendingUp className="w-5 h-5 text-blue-400 mx-auto mb-1" />
              <p className="text-xs text-slate-400">Recuperación Neta</p>
              <p className={`text-lg font-bold ${
                simulation.net_recovery.net_if_win_full > 0 ? 'text-green-400' : 'text-red-400'
              }`}>
                ${simulation.net_recovery.net_if_win_full?.toLocaleString()}
              </p>
            </div>
          </div>

          {/* Timeline */}
          <div>
            <h4 className="text-white font-medium mb-3 flex items-center space-x-2">
              <Clock className="w-4 h-4 text-blue-400" />
              <span>Línea de Tiempo Procesal</span>
            </h4>
            <div className="bg-slate-700/30 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm text-slate-400">{simulation.timeline.procedure_name}</span>
                <span className="text-sm text-white">
                  {simulation.timeline.estimated_duration.min_months} - {simulation.timeline.estimated_duration.max_months} meses
                </span>
              </div>
              <div className="space-y-2">
                {simulation.timeline.stages?.slice(0, 5).map((stage, idx) => (
                  <div key={idx} className="flex items-center space-x-3">
                    <div className="w-2 h-2 rounded-full bg-blue-500" />
                    <span className="text-sm text-slate-300 flex-1">{stage.stage}</span>
                    <span className="text-xs text-slate-500">{stage.duration_days} días</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Recommendations */}
          <div>
            <h4 className="text-white font-medium mb-3">Recomendaciones</h4>
            <div className="space-y-2">
              {simulation.recommendations?.map((rec, idx) => (
                <div key={idx} className="p-3 bg-slate-700/50 rounded-lg">
                  <div className="flex items-center space-x-2 mb-1">
                    <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                      rec.priority === 'ALTA' ? 'bg-red-500 text-white' : 'bg-blue-500 text-white'
                    }`}>
                      {rec.priority}
                    </span>
                    <span className="text-sm text-white font-medium">{rec.action}</span>
                  </div>
                  <p className="text-xs text-slate-400">{rec.reason}</p>
                  {rec.next_steps && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {rec.next_steps.map((step, stepIdx) => (
                        <span key={stepIdx} className="text-xs bg-slate-600 text-slate-300 px-2 py-0.5 rounded">
                          {step}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Settlement Suggestion */}
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
            <h4 className="text-blue-400 font-medium mb-2 flex items-center space-x-2">
              <MessageSquare className="w-4 h-4" />
              <span>Sugerencia de Acuerdo Extrajudicial</span>
            </h4>
            <p className="text-sm text-slate-300">
              Monto recomendado: <span className="text-white font-bold">${simulation.net_recovery.settlement_suggestion?.recommended?.toLocaleString()}</span>
            </p>
            <p className="text-xs text-slate-400 mt-1">{simulation.net_recovery.settlement_suggestion?.reason}</p>
          </div>
        </motion.div>
      )}
    </div>
  );
};

// ==================== IDENTITY VERIFIER COMPONENT ====================

const IdentityVerifier = () => {
  const [rut, setRut] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const formatRut = (value) => {
    let cleaned = value.replace(/[^0-9kK]/g, '').toUpperCase();
    if (cleaned.length > 1) {
      const body = cleaned.slice(0, -1);
      const dv = cleaned.slice(-1);
      let formatted = '';
      for (let i = body.length - 1, j = 0; i >= 0; i--, j++) {
        if (j > 0 && j % 3 === 0) formatted = '.' + formatted;
        formatted = body[i] + formatted;
      }
      return `${formatted}-${dv}`;
    }
    return cleaned;
  };

  const verifyIdentity = async () => {
    if (!rut || rut.length < 9) {
      toast.error('Ingresa un RUT válido');
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post('/identity/verify', { rut });
      setResult(data);
      if (data.success) {
        toast.success('Verificación completada');
      } else {
        toast.error(data.error || 'Error en verificación');
      }
    } catch (error) {
      toast.error('Error al verificar identidad');
    }
    setLoading(false);
  };

  const exportPDF = async () => {
    if (!result || !result.success) return;
    setExporting(true);
    try {
      // Request verification code from backend
      const verificationData = await requestVerificationCode('identity', result, {
        rut: result.rut,
        risk_level: result.risk_score?.level
      });
      
      // Generate PDF with verification code
      const doc = await generateIdentityPDF(result, verificationData.verification_code);
      downloadPDF(doc, `verificacion-${verificationData.verification_code}.pdf`);
      toast.success(`PDF verificado generado. Código: ${verificationData.verification_code}`);
    } catch (error) {
      console.error('Export error:', error);
      // Fallback without verification
      const doc = await generateIdentityPDF(result, null);
      downloadPDF(doc, `verificacion-${result.rut?.replace(/\./g, '')}-${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success('PDF descargado');
    }
    setExporting(false);
  };

  const getAlertIcon = (type) => {
    const icons = {
      'CRÍTICO': <AlertOctagon className="w-5 h-5 text-red-500" />,
      'ALTO': <AlertTriangle className="w-5 h-5 text-orange-500" />,
      'MEDIO': <Info className="w-5 h-5 text-yellow-500" />,
      'INFO': <Info className="w-5 h-5 text-blue-500" />
    };
    return icons[type] || icons['INFO'];
  };

  return (
    <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-cyan-500/20 rounded-lg">
            <User className="w-6 h-6 text-cyan-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">Verificación de Identidad</h3>
            <p className="text-sm text-slate-400">Consulta en tiempo real a bases de datos oficiales</p>
          </div>
        </div>
        {result && result.success && (
          <button
            onClick={exportPDF}
            disabled={exporting}
            className="px-3 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg flex items-center space-x-2 disabled:opacity-50"
            title="Exportar PDF con código de verificación legal"
          >
            {exporting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <ShieldCheck className="w-4 h-4" />
            )}
            <span>PDF Verificado</span>
          </button>
        )}
      </div>

      {/* Input */}
      <div className="flex space-x-2 mb-6">
        <input
          type="text"
          value={rut}
          onChange={(e) => setRut(formatRut(e.target.value))}
          placeholder="12.345.678-9"
          maxLength={12}
          className="flex-1 px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-500"
        />
        <button
          onClick={verifyIdentity}
          disabled={loading || rut.length < 9}
          className="px-4 py-2 bg-cyan-600 hover:bg-cyan-500 text-white rounded-lg flex items-center space-x-2 disabled:opacity-50"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          <span>Verificar</span>
        </button>
      </div>

      {result && result.success && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-6"
        >
          {/* Mock Data Warning */}
          {result.is_mock_data && (
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 flex items-center space-x-2">
              <Info className="w-4 h-4 text-yellow-400" />
              <span className="text-sm text-yellow-300">Datos de demostración - En producción se conecta a servicios reales</span>
            </div>
          )}

          {/* Risk Score */}
          <div className={`rounded-xl p-4 ${
            result.risk_score.level === 'BAJO' ? 'bg-green-500/10 border border-green-500/30' :
            result.risk_score.level === 'MODERADO' ? 'bg-yellow-500/10 border border-yellow-500/30' :
            result.risk_score.level === 'ALTO' ? 'bg-orange-500/10 border border-orange-500/30' :
            'bg-red-500/10 border border-red-500/30'
          }`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400">Score de Riesgo para Contratar</p>
                <p className={`text-3xl font-bold ${
                  result.risk_score.color === 'green' ? 'text-green-400' :
                  result.risk_score.color === 'yellow' ? 'text-yellow-400' :
                  result.risk_score.color === 'orange' ? 'text-orange-400' : 'text-red-400'
                }`}>
                  {result.risk_score.score}/100
                </p>
              </div>
              <div className="text-right">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  result.risk_score.level === 'BAJO' ? 'bg-green-500/20 text-green-400' :
                  result.risk_score.level === 'MODERADO' ? 'bg-yellow-500/20 text-yellow-400' :
                  result.risk_score.level === 'ALTO' ? 'bg-orange-500/20 text-orange-400' :
                  'bg-red-500/20 text-red-400'
                }`}>
                  Riesgo {result.risk_score.level}
                </span>
              </div>
            </div>
            <p className="text-sm text-slate-400 mt-2">{result.risk_score.recommendation}</p>
          </div>

          {/* Alerts */}
          {result.alerts?.length > 0 && (
            <div className="space-y-2">
              {result.alerts.map((alert, idx) => (
                <div key={idx} className={`p-3 rounded-lg flex items-start space-x-3 ${
                  alert.type === 'CRÍTICO' ? 'bg-red-500/10 border border-red-500/30' :
                  alert.type === 'ALTO' ? 'bg-orange-500/10 border border-orange-500/30' :
                  alert.type === 'MEDIO' ? 'bg-yellow-500/10 border border-yellow-500/30' :
                  'bg-blue-500/10 border border-blue-500/30'
                }`}>
                  {getAlertIcon(alert.type)}
                  <div className="flex-1">
                    <p className="font-medium text-white">{alert.title}</p>
                    <p className="text-sm text-slate-400">{alert.message}</p>
                    <p className="text-xs text-slate-500 mt-1">Acción: {alert.action}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Identity Info */}
          <div className="bg-slate-700/50 rounded-lg p-4">
            <h4 className="text-white font-medium mb-3 flex items-center space-x-2">
              <User className="w-4 h-4 text-blue-400" />
              <span>Identidad Verificada</span>
              {result.identity.verified && <CheckCircle className="w-4 h-4 text-green-400" />}
            </h4>
            {result.identity.type === 'persona' ? (
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-slate-400">Nombre</p>
                  <p className="text-white">{result.identity.nombre_completo}</p>
                </div>
                <div>
                  <p className="text-slate-400">RUT</p>
                  <p className="text-white">{result.rut}</p>
                </div>
                <div>
                  <p className="text-slate-400">Nacionalidad</p>
                  <p className="text-white">{result.identity.nacionalidad}</p>
                </div>
                <div>
                  <p className="text-slate-400">Estado Civil</p>
                  <p className="text-white">{result.identity.estado_civil}</p>
                </div>
              </div>
            ) : (
              <div className="space-y-2 text-sm">
                <div>
                  <p className="text-slate-400">Razón Social</p>
                  <p className="text-white">{result.identity.razon_social}</p>
                </div>
                <div>
                  <p className="text-slate-400">Actividades</p>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {result.identity.actividades?.map((act, idx) => (
                      <span key={idx} className="px-2 py-0.5 bg-slate-600 text-slate-300 rounded text-xs">
                        {act}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Financial Status */}
          <div className="bg-slate-700/50 rounded-lg p-4">
            <h4 className="text-white font-medium mb-3 flex items-center space-x-2">
              <CreditCard className="w-4 h-4 text-green-400" />
              <span>Situación Financiera</span>
            </h4>
            {result.financial_status.credit_score && (
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-slate-400">Score Crediticio</p>
                  <p className={`text-xl font-bold ${
                    result.financial_status.credit_score.score >= 600 ? 'text-green-400' :
                    result.financial_status.credit_score.score >= 400 ? 'text-yellow-400' : 'text-red-400'
                  }`}>
                    {result.financial_status.credit_score.score}
                  </p>
                </div>
                <div>
                  <p className="text-slate-400">Clasificación</p>
                  <p className="text-white text-xl font-bold">{result.financial_status.credit_score.classification}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-slate-400 text-xs">{result.financial_status.credit_score.interpretation}</p>
                </div>
              </div>
            )}
            {result.financial_status.morosities?.length > 0 && (
              <div className="mt-3 pt-3 border-t border-slate-600">
                <p className="text-red-400 font-medium text-sm mb-2">Morosidades ({result.financial_status.morosities.length})</p>
                {result.financial_status.morosities.map((m, idx) => (
                  <div key={idx} className="flex items-center justify-between text-sm py-1">
                    <span className="text-slate-300">{m.acreedor}</span>
                    <span className="text-red-400">${m.monto.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Litigation History */}
          {(result.litigation_history.active_cases.count > 0 || result.litigation_history.historical_cases.count > 0) && (
            <div className="bg-slate-700/50 rounded-lg p-4">
              <h4 className="text-white font-medium mb-3 flex items-center space-x-2">
                <Gavel className="w-4 h-4 text-amber-400" />
                <span>Historial de Litigios</span>
              </h4>
              <div className="grid grid-cols-2 gap-4 text-center mb-3">
                <div className="bg-slate-600/50 rounded-lg p-2">
                  <p className="text-2xl font-bold text-amber-400">{result.litigation_history.active_cases.count}</p>
                  <p className="text-xs text-slate-400">Causas Activas</p>
                </div>
                <div className="bg-slate-600/50 rounded-lg p-2">
                  <p className="text-2xl font-bold text-slate-300">{result.litigation_history.historical_cases.count}</p>
                  <p className="text-xs text-slate-400">Causas Históricas</p>
                </div>
              </div>
              {result.litigation_history.active_cases.cases?.map((caso, idx) => (
                <div key={idx} className="text-sm bg-slate-600/30 rounded p-2 mb-1">
                  <div className="flex justify-between">
                    <span className="text-white">{caso.rol}</span>
                    <span className={`text-xs px-2 py-0.5 rounded ${
                      caso.calidad === 'Demandado' ? 'bg-red-500/20 text-red-400' : 'bg-blue-500/20 text-blue-400'
                    }`}>
                      {caso.calidad}
                    </span>
                  </div>
                  <p className="text-slate-400 text-xs">{caso.materia} - {caso.tribunal}</p>
                </div>
              ))}
            </div>
          )}

          {/* Assets */}
          {(result.assets.real_estate.count > 0 || result.assets.vehicles.count > 0) && (
            <div className="bg-slate-700/50 rounded-lg p-4">
              <h4 className="text-white font-medium mb-3 flex items-center space-x-2">
                <Building2 className="w-4 h-4 text-purple-400" />
                <span>Bienes Registrados</span>
              </h4>
              <div className="grid grid-cols-2 gap-4 text-center">
                <div className="bg-slate-600/50 rounded-lg p-2">
                  <p className="text-2xl font-bold text-purple-400">{result.assets.real_estate.count}</p>
                  <p className="text-xs text-slate-400">Propiedades</p>
                </div>
                <div className="bg-slate-600/50 rounded-lg p-2">
                  <p className="text-2xl font-bold text-blue-400">{result.assets.vehicles.count}</p>
                  <p className="text-xs text-slate-400">Vehículos</p>
                </div>
              </div>
              {result.assets.real_estate.total_encumbered > 0 && (
                <p className="text-xs text-yellow-400 mt-2">
                  Gravámenes: ${result.assets.real_estate.total_encumbered.toLocaleString()}
                </p>
              )}
            </div>
          )}
        </motion.div>
      )}

      {result && !result.success && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
          <div className="flex items-center space-x-2 text-red-400">
            <XCircle className="w-5 h-5" />
            <span className="font-medium">{result.error}</span>
          </div>
          {result.details && <p className="text-sm text-slate-400 mt-1">{result.details}</p>}
        </div>
      )}
    </div>
  );
};


// ==================== MAIN EXPORT: DISRUPTIVE TOOLS PAGE ====================

export { LitigationPredictor, PowerMapAnalyzer, ScenarioSimulator, IdentityVerifier };

const DisruptiveToolsPage = () => {
  const [activeTab, setActiveTab] = useState('litigation');
  const [contractContent, setContractContent] = useState('');

  const tabs = [
    { id: 'litigation', name: 'Predictor de Litigios', icon: AlertTriangle, color: 'red' },
    { id: 'power', name: 'Mapa de Poder', icon: Scale, color: 'purple' },
    { id: 'simulator', name: 'Simulador', icon: Activity, color: 'amber' },
    { id: 'negotiator', name: 'Negociador', icon: Users, color: 'emerald' },
    { id: 'identity', name: 'Verificación', icon: User, color: 'cyan' },
    { id: 'contract-analysis', name: 'Analizar Contrato', icon: Search, color: 'teal', link: '/analizar-contrato' },
    { id: 'clause-comparator', name: 'Comparar Cláusulas', icon: ArrowLeftRight, color: 'indigo', link: '/comparador-clausulas' },
    { id: 'negotiation-assistant', name: 'Asistente Negociación', icon: MessageSquare, color: 'blue', link: '/negociacion' }
  ];

  return (
    <div className="min-h-screen bg-slate-900 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Back to Dashboard Button */}
        <div className="mb-6">
          <Link 
            to="/dashboard" 
            className="inline-flex items-center space-x-2 text-slate-400 hover:text-white transition-colors group"
          >
            <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
            <Home className="w-4 h-4" />
            <span>Volver al Dashboard</span>
          </Link>
        </div>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Herramientas Disruptivas</h1>
          <p className="text-slate-400">Análisis legal avanzado impulsado por IA</p>
        </div>

        {/* Tabs */}
        <div className="flex space-x-2 mb-6 overflow-x-auto pb-2">
          {tabs.map(tab => (
            tab.link ? (
              <Link
                key={tab.id}
                to={tab.link}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors whitespace-nowrap bg-${tab.color}-500/20 text-${tab.color}-400 border border-${tab.color}-500/30 hover:bg-${tab.color}-500/30`}
              >
                <tab.icon className="w-4 h-4" />
                <span>{tab.name}</span>
                <ChevronRight className="w-4 h-4" />
              </Link>
            ) : (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? `bg-${tab.color}-500/20 text-${tab.color}-400 border border-${tab.color}-500/30`
                    : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                <span>{tab.name}</span>
              </button>
            )
          ))}
        </div>

        {/* Content Input (for tools that need contract content) */}
        {(activeTab === 'litigation' || activeTab === 'power' || activeTab === 'simulator') && (
          <div className="mb-6">
            <label className="text-sm text-slate-400 block mb-2">Contenido del contrato para analizar:</label>
            <textarea
              value={contractContent}
              onChange={(e) => setContractContent(e.target.value)}
              placeholder="Pega aquí el contenido del contrato para analizarlo con las herramientas de IA..."
              className="w-full h-32 px-4 py-3 bg-slate-800 border border-slate-700 rounded-xl text-white placeholder-slate-500 resize-none focus:outline-none focus:border-blue-500"
            />
          </div>
        )}

        {/* Active Tool */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            {activeTab === 'litigation' && <LitigationPredictor content={contractContent} />}
            {activeTab === 'power' && <PowerMapAnalyzer content={contractContent} />}
            {activeTab === 'simulator' && <ScenarioSimulator content={contractContent} />}
            {activeTab === 'negotiator' && <ClauseNegotiator />}
            {activeTab === 'identity' && <IdentityVerifier />}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

export default DisruptiveToolsPage;
